import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Gift, Clock, CheckCircle, Loader2, Users } from 'lucide-react';
import api from '../../services/api';

const ClientCreditsHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchTransactions();
  }, [filterStatus]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await api.get(`/client/my-financial-transactions${params}`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching credit history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { label: 'En attente', color: 'bg-[#FFB800]/10 text-[#FFB800]', icon: Clock },
      'available': { label: 'Disponible', color: 'bg-[#00FF88]/10 text-[#00FF88]', icon: CheckCircle },
      'used': { label: 'Utilisé', color: 'bg-[#00FFFF]/10 text-[#00FFFF]', icon: CheckCircle }
    };
    const badge = statusMap[status] || { label: status, color: 'bg-gray-500/10 text-gray-500', icon: Clock };
    const Icon = badge.icon;
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color} flex items-center gap-1 w-fit`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#00FFFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link 
            to="/client/dashboard" 
            className="inline-flex items-center gap-2 text-[#00FFFF] hover:text-[#00E5E5] mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour au dashboard
          </Link>
          <h1 className="text-4xl font-bold text-white mb-2">🎁 Historique des Gains</h1>
          <p className="text-[#B8B8BA]">Tous vos crédits de parrainage</p>
        </motion.div>

        {/* Filtres */}
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-[#B8B8BA]">Filtrer par statut:</span>
            {['all', 'pending', 'available', 'used'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  filterStatus === status
                    ? 'bg-[#00FFFF] text-[#0A0A0B] font-semibold'
                    : 'bg-[#222226] text-[#B8B8BA] hover:bg-[#3A3A3D]'
                }`}
              >
                {status === 'all' ? 'Tous' : 
                 status === 'pending' ? 'En attente' :
                 status === 'available' ? 'Disponible' :
                 'Utilisés'}
              </button>
            ))}
          </div>
        </div>

        {/* Tableau des gains */}
        <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Historique des crédits</h2>
            <span className="bg-[#00FFFF]/10 text-[#00FFFF] px-4 py-2 rounded-full font-semibold">
              {transactions.length} crédit{transactions.length > 1 ? 's' : ''}
            </span>
          </div>

          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <Gift className="h-16 w-16 text-[#3A3A3D] mx-auto mb-4" />
              <p className="text-[#B8B8BA]">Aucun crédit pour ce filtre</p>
              <p className="text-[#6B6B6E] text-sm mt-2">Parrainez vos amis pour gagner des crédits !</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#222226]">
                  <tr>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Date</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Type</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Filleul</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Montant</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Statut</th>
                    <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Référence</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => {
                    const level = tx.metadata?.level || 'n1';
                    
                    return (
                      <tr key={tx.id} className="border-t border-[#3A3A3D] hover:bg-[#222226] transition-colors">
                        <td className="px-6 py-4 text-[#B8B8BA] text-sm">
                          {formatDate(tx.created_at)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-[#FF6B9D]" />
                            <span className="text-white">
                              Parrainage {level === 'n1' ? 'direct' : 'N2'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-[#B8B8BA]">
                          {tx.metadata?.referred_email || tx.metadata?.referred_name || '—'}
                        </td>
                        <td className="px-6 py-4 font-bold text-[#00FF88]">
                          +{tx.amount.toFixed(2)}€
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(tx.status)}
                        </td>
                        <td className="px-6 py-4 text-[#6B6B6E] text-xs font-mono">
                          {tx.source_id?.substring(0, 12) || tx.id.substring(0, 12)}...
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientCreditsHistory;
